export type MapApp = "google" | "apple" | "waze";
export type DevicePlatform = "ios" | "android" | "windows" | "macos" | "linux" | "unknown";

export const MAP_URL_TEMPLATES: Record<MapApp, string> = {
  google: "https://www.google.com/maps/dir/?api=1&destination={lat},{lng}",
  apple: "https://maps.apple.com/?daddr={lat},{lng}",
  waze: "https://waze.com/ul?ll={lat},{lng}&navigate=yes",
};

export function formatDirectionUrl(app: MapApp, lat: number, lng: number): string {
  return MAP_URL_TEMPLATES[app]
    .replace("{lat}", String(lat))
    .replace("{lng}", String(lng));
}

export function detectDevicePlatform(userAgent = window.navigator.userAgent): DevicePlatform {
  if (/android/i.test(userAgent)) return "android";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "ios";
  if (/Windows/i.test(userAgent)) return "windows";
  if (/Macintosh|Mac OS X/i.test(userAgent)) return "macos";
  if (/Linux/i.test(userAgent)) return "linux";
  return "unknown";
}

function formatGoogleIntent(platform: DevicePlatform, lat: number, lng: number): string {
  if (platform === "android") return `google.navigation:q=${lat},${lng}`;
  if (platform === "ios") return `comgooglemaps://?daddr=${lat},${lng}&directionsmode=driving`;
  return formatDirectionUrl("google", lat, lng);
}

function formatAppleIntent(platform: DevicePlatform, lat: number, lng: number): string {
  if (platform === "ios" || platform === "macos") return `maps://?daddr=${lat},${lng}`;
  return formatDirectionUrl("apple", lat, lng);
}

function formatWazeIntent(platform: DevicePlatform, lat: number, lng: number): string {
  if (platform === "android" || platform === "ios") return `waze://?ll=${lat},${lng}&navigate=yes`;
  return formatDirectionUrl("waze", lat, lng);
}

export function formatDirectionIntentUrl(
  app: MapApp,
  lat: number,
  lng: number,
  platform: DevicePlatform = detectDevicePlatform(),
): string {
  if (app === "google") return formatGoogleIntent(platform, lat, lng);
  if (app === "apple") return formatAppleIntent(platform, lat, lng);
  return formatWazeIntent(platform, lat, lng);
}

export function openDirectionsWithFallback(
  app: MapApp,
  lat: number,
  lng: number,
  platform: DevicePlatform = detectDevicePlatform(),
) {
  const intentUrl = formatDirectionIntentUrl(app, lat, lng, platform);
  const fallbackUrl = formatDirectionUrl(app, lat, lng);

  if (intentUrl === fallbackUrl) {
    window.open(fallbackUrl, "_blank", "noopener,noreferrer");
    return;
  }

  let cancelled = false;
  const cancelFallback = () => {
    cancelled = true;
    window.clearTimeout(fallbackTimer);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
  const onVisibilityChange = () => {
    if (document.hidden) {
      cancelFallback();
    }
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  const fallbackTimer = window.setTimeout(() => {
    if (!cancelled) {
      window.location.assign(fallbackUrl);
    }
    document.removeEventListener("visibilitychange", onVisibilityChange);
  }, 1200);

  window.location.assign(intentUrl);
}
